import type { CSSProperties } from "react";
import { useState, useEffect } from "react";

import styled from "styled-components";

import { setFontVariant, colorsPalettes } from "@similarweb/styles";

import { translate } from "app/filters/i18nFilter";

const PLOT_RIGHT_PADDING = 24;
const TITLES_VERTICAL_PADDING = 12;
const TITLES_HORIZONTAL_PADDING = 12;
const TITLE_ELEMENT_WIDTH = 16;

const titles = {
    get topLeft() {
        return translate("demand.analysis.search.trends.keywords.breakdown.scatterPlot.title.topLeft");
    },
    get topRight() {
        return translate("demand.analysis.search.trends.keywords.breakdown.scatterPlot.title.topRight");
    },
    get bottomLeft() {
        return translate("demand.analysis.search.trends.keywords.breakdown.scatterPlot.title.bottomLeft");
    },
    get bottomRight() {
        return translate("demand.analysis.search.trends.keywords.breakdown.scatterPlot.title.bottomRight");
    },
} as const;
type TitlesPositionKey = keyof typeof titles;

interface PositionProperties {
    top?: CSSProperties["top"];
    left?: CSSProperties["left"];
    right?: CSSProperties["right"];
    bottom?: CSSProperties["bottom"];
}
interface TitleTextProps extends PositionProperties {
    position: TitlesPositionKey;
}
const TitleText = styled.div<TitleTextProps>`
    ${setFontVariant("input2", colorsPalettes.carbon[250])};
    margin-right: 4px;
    position: absolute;
    width: 52px;
    text-align: ${({ position }) => (position === "topLeft" || position === "bottomLeft" ? "left" : "right")};
    ${({ left }) => (left ? `left: ${left}px` : "")};
    ${({ right }) => (right ? `right: ${right}px` : "")};
    ${({ top }) => (top ? `top: ${top}px` : "")};
    ${({ bottom }) => (bottom ? `bottom: ${bottom}px` : "")};
`;

interface KeywordGroupsScatterPlotTitlesProps {
    chartInstance: Highcharts.Chart | null;
}

export const KeywordGroupsScatterPlotTitles = ({ chartInstance }: KeywordGroupsScatterPlotTitlesProps) => {
    const [titlesPositions, setTitlePositions] = useState<Record<TitlesPositionKey, PositionProperties>>();

    useEffect(() => {
        if (!chartInstance) {
            return;
        }

        const topLeft = {
            top: chartInstance.plotTop + TITLES_VERTICAL_PADDING,
            left: chartInstance.plotLeft + TITLES_HORIZONTAL_PADDING,
        };
        const topRight = {
            top: chartInstance.plotTop + TITLES_VERTICAL_PADDING,
            left:
                chartInstance.plotLeft +
                chartInstance.plotWidth -
                TITLES_HORIZONTAL_PADDING * 2 -
                PLOT_RIGHT_PADDING -
                TITLE_ELEMENT_WIDTH,
        };
        const bottomLeft = {
            top: chartInstance.plotTop + chartInstance.plotHeight - TITLES_VERTICAL_PADDING * 2,
            left: chartInstance.plotLeft + TITLES_HORIZONTAL_PADDING,
        };
        const bottomRight = {
            top: chartInstance.plotTop + chartInstance.plotHeight - TITLES_VERTICAL_PADDING * 2,
            left:
                chartInstance.plotLeft +
                chartInstance.plotWidth -
                TITLES_HORIZONTAL_PADDING * 2 -
                PLOT_RIGHT_PADDING -
                TITLE_ELEMENT_WIDTH,
        };

        setTitlePositions({
            topLeft,
            topRight,
            bottomLeft,
            bottomRight,
        });
    }, [
        chartInstance,
        chartInstance?.plotTop,
        chartInstance?.plotLeft,
        chartInstance?.plotWidth,
        chartInstance?.plotHeight,
    ]);

    return (
        <>
            {titlesPositions
                ? Object.entries(titlesPositions).map(([position, { top, left, right }]) => (
                      <TitleText
                          key={position}
                          position={position as TitlesPositionKey}
                          top={top}
                          left={left}
                          right={right}
                      >
                          {titles[position]}
                      </TitleText>
                  ))
                : null}
        </>
    );
};
